import { useState } from "react";
import {
    Box,
    Grid,
    Typography,
    SxProps,
    Theme,
    Card,
    CardContent,
    IconButton,
    Stack,
} from "@mui/material";
import { usePosition, useIsMobile } from "../hooks";
import { useNavigate, useParams } from "react-router-dom";
import CareersForm from "../components/CareersForm";
import { CommonStyles } from "../styles/CommonStyles";
import JobIcon from "../components/icons/JobIcon";
import LocationIcon from "../components/icons/LocationIcon";
import SuccessPopup from "../components/SuccessPopup";
import ErrorPopup from "../components/ErrorPopup";
import PageTitle from "../components/PageTitle";
import PageContent from "../components/PageContent";
import { useTranslation } from "react-i18next";
import backImg from "../assets/back.webp";
import backImgMobile from "../assets/back-mobile.webp";

const CareersJobPositionDetailPageStyle: Record<string, SxProps<Theme>> = {
    descriptionText: {
        fontSize: "1rem",
        textAlign: "left",
        lineHeight: "3rem",
    },
    descriptionTextMobile: {
        fontSize: "16px",
        textAlign: "left",
    },
};

type CareersJobPositionDetailPageParams = {
    positionId: string;
};

const CareersJobPositionDetailPage = () => {
    const { isMobile } = useIsMobile();
    const { t } = useTranslation();
    const { positionId } = useParams<CareersJobPositionDetailPageParams>();
    const { position, postPositionApply } = usePosition(Number(positionId));
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupErrorOpen, setPopupErrorOpen] = useState(false);

    let navigate = useNavigate();

    const submitForm = (name: string, email: string, cv: string) => {
        postPositionApply({ jobId: Number(positionId), name, email, cv })
            .then((res) => {
                setPopupOpen(true);
            })
            .catch((e) => {
                setPopupErrorOpen(true);
            });
    };

    return (
        <>
            <SuccessPopup
                open={popupOpen}
                text={t("careersdetails.thanks")}
                handleClose={() => {
                    setPopupOpen(false);
                    navigate("/careers/positions");
                }}
            />
            <ErrorPopup
                open={popupErrorOpen}
                text="Error"
                handleClose={() => setPopupErrorOpen(true)}
            />
            
            {/* Titolo e pulsante back */}
            <Grid container>
                <IconButton
                    size="large"
                    edge="start"
                    color="inherit"
                    aria-label="menu"
                    sx={isMobile ? CommonStyles.backMobile : CommonStyles.back}
                    onClick={() => navigate("/careers/positions")}
                >
                    <img src={isMobile ? backImgMobile : backImg} />
                </IconButton>
                <PageTitle>{t("careers.title")}</PageTitle>
            </Grid>

            <PageContent>
                {
                    isMobile ? <Stack width={"100%"} spacing={2}>
                        <Card sx={CommonStyles.cardContainerMobile}>
                            <CardContent>
                                <Typography sx={[CommonStyles.cardTitleMobile, { textAlign: "left" }].flat(2)}>
                                    {position?.title}
                                </Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        flexDirection: "row",
                                        marginBottom: "5px",
                                        marginLeft: "15px",
                                    }}
                                >
                                    <JobIcon sx={{ marginRight: "10px" }} />
                                    <Typography sx={CommonStyles.cardSubtitleMobile}>
                                        {position?.employment}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        flexDirection: "row",
                                        marginLeft: "15px",
                                    }}
                                >
                                    <LocationIcon sx={{ marginRight: "10px" }} />
                                    <Typography sx={CommonStyles.cardSubtitleMobile}>
                                        {position?.location}
                                    </Typography>
                                </Box>
                                <Box sx={{ marginLeft: "15px", marginTop: "15px"}}>
                                    <Typography sx={CareersJobPositionDetailPageStyle.descriptionTextMobile}>
                                        {position?.description.split("\n").map((line) => (
                                            <Typography sx={CareersJobPositionDetailPageStyle.descriptionTextMobile}>
                                                {line}
                                            </Typography>
                                        ))}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                        <Card sx={CommonStyles.cardContainerMobile}>
                            <CardContent>
                                <CareersForm positionName={position?.title || ""} />
                            </CardContent>
                        </Card>
                    </Stack> 
                    
                
                
                :
                

                <Grid container spacing={4} sx={{ alignItems: "center" }}>
                    <Grid item md={12} container spacing={8}>
                        <Grid item lg={8} md={12} sm={12}>
                            <Card
                                sx={[
                                    isMobile
                                        ? CommonStyles.cardContainerMobile
                                        : CommonStyles.cardContainer,
                                    !isMobile ? { height: "70vh", overflow: "hidden" } : {},
                                ].flat(2)}
                            >
                                <CardContent>
                                    <Typography
                                        sx={[
                                            isMobile
                                                ? CommonStyles.cardTitleMobile
                                                : CommonStyles.cardTitle,
                                            { textAlign: "left" },
                                        ].flat(2)}
                                    >
                                        {position?.title}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            flexDirection: "row",
                                            marginBottom: "5px",
                                            marginLeft: "15px",
                                        }}
                                    >
                                        <JobIcon sx={{ marginRight: "10px" }} />
                                        <Typography
                                            sx={
                                                isMobile
                                                    ? CommonStyles.cardSubtitleMobile
                                                    : CommonStyles.cardSubtitle
                                            }
                                        >
                                            {position?.employment}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            flexDirection: "row",
                                            marginLeft: "15px",
                                        }}
                                    >
                                        <LocationIcon sx={{ marginRight: "10px" }} />
                                        <Typography
                                            sx={
                                                isMobile
                                                    ? CommonStyles.cardSubtitleMobile
                                                    : CommonStyles.cardSubtitle
                                            }
                                        >
                                            {position?.location}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            marginLeft: "15px",
                                            marginTop: "15px",
                                            height: "50vh",
                                            overflow: "auto",
                                            "&::-webkit-scrollbar": {
                                                borderRadius: 2,
                                                width: "0.5em",
                                                border: "1px solid white",
                                            },
                                            "&::-webkit-scrollbar-track": {
                                                background: "#81BC0047",
                                                borderRadius: "10px",
                                            },
                                            "&::-webkit-scrollbar-thumb": {
                                                background: "#81BC00",
                                                borderRadius: "5px",
                                                border: "1px solid white",
                                            },
                                            "&::-webkit-scrollbar-thumb:hover": {
                                                background: "#81BC00",
                                            },
                                        }}
                                    >
                                        <Typography
                                            sx={
                                                isMobile
                                                    ? CareersJobPositionDetailPageStyle.descriptionTextMobile
                                                    : CareersJobPositionDetailPageStyle.descriptionText
                                            }
                                        >
                                            {position?.description.split("\n").map((line) => (
                                                <Typography
                                                    sx={
                                                        isMobile
                                                            ? CareersJobPositionDetailPageStyle.descriptionTextMobile
                                                            : CareersJobPositionDetailPageStyle.descriptionText
                                                    }
                                                >
                                                    {line}
                                                </Typography>
                                            ))}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item lg={4} md={12} sm={12}>
                            <Card
                                sx={[
                                    isMobile
                                        ? CommonStyles.cardContainerMobile
                                        : CommonStyles.cardContainer,
                                    !isMobile ? { overflow: "hidden" } : {},
                                ].flat(2)}
                            >
                                <CardContent sx={{ height: "100%" }}>
                                    <CareersForm positionName={position?.title || ""} />
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Grid>
                }
            </PageContent>
        </>
    );
};

export default CareersJobPositionDetailPage;
